import React, { useState, useEffect } from "react";
import "../../../../../css/CashflowModeller2/DialogBoxes/DialogBoxes.css";
import Close from "../Buttons/Close";
import Input2 from "../Inputs/Input2";
import InputSmall from "../Inputs/InputSmall";
import AddButton from "../Buttons/AddButton";
import RemoveButton from "../Buttons/RemoveButton";
import { useCfmContext } from "../../Context/CfmContext";
import IncomeTypeOptions from "../../Utils/IncomeTypes";
import TimelineOptions from "../../Utils/TimelineOptions";
import useNameOptions from "../../Utils/NameOptions";
import useDraggableDialog from "./Utils/UseDraggableDialog";
import Input2Medium from "../Inputs/Input2Medium";
import e from "cors";

const STATE_PENSION_ANNUAL = 11973;
const getStatePensionAmountForUnit = (unit = "Annual") =>
  unit === "Monthly"
    ? Number((STATE_PENSION_ANNUAL / 12).toFixed(2))
    : STATE_PENSION_ANNUAL;

const defaultRow = {
  name: "",
  type: "",
  amount: { value: "", unit: "Annual" },
  start: "",
  end: "",
};

const IncomeDialogBox = ({ onClose }) => {
  const { state, dispatch } = useCfmContext();
  const [inputRow, setInputRow] = useState({ ...defaultRow });
  const [rows, setRows] = useState(state.income || []);
  const nameOptions = useNameOptions();
  const { ref, position, handleMouseDown } = useDraggableDialog();
  const [startEndError, setStartEndError] = useState(false);

  const handleInputChange = (field, value) => {
    setInputRow((prev) => {
      // Special handling for amount as it's an object
      if (field === "amount") {
        const next = { ...prev, amount: { ...prev.amount, ...value } };

        // If "State Pension", ignore manual value edits; only recalc from unit
        // if (prev.type === "State Pension") {
        //   const unit = value.unit ?? prev.amount.unit ?? "Annual";
        //   next.amount = {
        //     unit,
        //     value: getStatePensionAmountForUnit(unit),
        //   };
        // }
        return next;
      }

      // If type changed to "State Pension", enforce rules immediately
      if (field === "type" && value === "State Pension") {
        const unit = "Annual";
        return {
          ...prev,
          type: value,
          end: "100",
          amount: {
            unit,
            value: getStatePensionAmountForUnit(unit),
          },
        };
      }

      return { ...prev, [field]: value };
    });
    setStartEndError(false);
  };

  const updateContext = (updated) => {
    setRows(updated);
    dispatch({ type: "UPDATE_INCOME", payload: updated });
  };

  const handleAdd = () => {
    const start = Number(inputRow.start);
    const end = Number(inputRow.end);
    const isStatePension = inputRow.type === "State Pension";

    const hasRangeError =
      !Number.isFinite(start) ||
      !Number.isFinite(end) ||
      start > end ||
      start < 18 ||
      end < 18 ||
      (isStatePension && start < 66); // start must be < 66 for State Pension

    if (hasRangeError) {
      setStartEndError(true);
      return;
    }

    // Final overwrite just in case, to ensure amount is correct for State Pension
    const rowToAdd = inputRow;
    // const rowToAdd = isStatePension
    //   ? {
    //       ...inputRow,
    //       end: "100",
    //       amount: {
    //         unit: inputRow.amount?.unit || "Annual",
    //         value: getStatePensionAmountForUnit(
    //           inputRow.amount?.unit || "Annual"
    //         ),
    //       },
    //     }
    //   : inputRow;

    updateContext([...rows, rowToAdd]);
    setInputRow({ ...defaultRow });
    setStartEndError(false);
  };

  const handleRowChange = (index, field, value) => {
    const updated = [...rows];
    const row = updated[index];

    if (field === "amount") {
      // If state pension, ignore manual value edits and only respond to unit change
      // if (row.type === "State Pension") {
      //   const nextUnit = value.unit ?? row.amount.unit ?? "Annual";
      //   updated[index] = {
      //     ...row,
      //     amount: {
      //       unit: nextUnit,
      //       value: getStatePensionAmountForUnit(nextUnit),
      //     },
      //   };
      // } else {
      updated[index] = {
        ...row,
        amount: { ...row.amount, ...value },
      };
      // }
    } else if (field === "type" && value === "State Pension") {
      // Switching an existing row to State Pension: enforce amount/end
      const unit = "Annual";
      updated[index] = {
        ...row,
        type: value,
        end: "100",
        amount: {
          unit,
          value: getStatePensionAmountForUnit(unit),
        },
      };
    } else {
      updated[index] = { ...row, [field]: value };
    }

    updateContext(updated);
  };

  const handleRemove = (index) => {
    updateContext(rows.filter((_, i) => i !== index));
  };

  const isIncomplete =
    !inputRow.name ||
    !inputRow.type ||
    (!inputRow.amount.value && inputRow.type !== "State Pension") ||
    !inputRow.start ||
    !inputRow.end;

  const isRowInvalid = (row) => {
    const { start, end, type } = row;
    if (!/^\d+$/.test(start) || !/^\d+$/.test(end)) return true;

    const s = parseInt(start, 10);
    const e = parseInt(end, 10);

    if (s > e || s < 18 || e < 18) return true;
    if (type === "State Pension" && s < 66) return true; // enforce start < 66

    return false;
  };

  // Keep end = 100 & correct amount while typing if inputRow is State Pension
  useEffect(() => {
    if (inputRow.type === "State Pension") {
      setInputRow((prev) => {
        const unit = prev.amount?.unit || "Annual";
        return {
          ...prev,
          end: "100",
          amount: {
            unit,
            value: getStatePensionAmountForUnit(unit),
          },
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRow.type]);

  const hasStatePensionForName = rows.some(
    (row) => row.name === inputRow.name && row.type === "State Pension"
  );

  return (
    <div
      ref={ref}
      className="dialog-box"
      style={{
        position: "absolute",
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      <div
        className="dialog-header"
        onMouseDown={handleMouseDown}
        style={{ cursor: "grab" }}
      >
        <div className="Title">Income</div>
        <div className="dialog-header-buttons">
          <div className="Content-Small" onClick={onClose}>
            Close
          </div>
          <div onClick={onClose}>
            <Close />
          </div>
        </div>
      </div>

      {/* Input Row */}
      <div className="dialog-row dialog-row-inputs">
        <Input2Medium
          type="invType"
          label="Name"
          value={inputRow.name}
          onChange={(val) => handleInputChange("name", val)}
          options={nameOptions}
        />
        <Input2Medium
          label="Type"
          value={inputRow.type}
          placeholderText="DB Pension"
          onChange={(val) => handleInputChange("type", val)}
          type="invType"
          options={
            hasStatePensionForName
              ? IncomeTypeOptions.filter((opt) => opt.value !== "State Pension")
              : IncomeTypeOptions
          }
        />
        <Input2Medium
          label="Amount"
          value={inputRow.amount.value}
          onChange={(val) =>
            // If State Pension, ignore manual value edits
            // inputRow.type === "State Pension"
            //   ? undefined
            //   :
            handleInputChange("amount", { value: val })
          }
          onUnitChange={(unit) => handleInputChange("amount", { unit })}
          options={TimelineOptions}
          type="number"
          currency
          // readOnly={inputRow.type === "State Pension"} // lock value edit
          currentUnit={inputRow.amount?.unit}
        />
        <InputSmall
          label="Start"
          value={inputRow.start}
          onChange={(val) => handleInputChange("start", val)}
          isError={
            inputRow.type === "State Pension" && Number(inputRow.start) < 66
          }
        />
        <InputSmall
          label="End"
          value={inputRow.end}
          onChange={(val) => handleInputChange("end", val)}
          isDisabled={inputRow.type === "State Pension"}
        />
        <AddButton onClick={handleAdd} isDisabled={isIncomplete} />
      </div>

      {startEndError && (
        <div className="error Label">
          Invalid age range.
        </div>
      )}
      {
        (inputRow.type === "State Pension" && Number(inputRow.start) < 66)
        && (
          <div className="error Label">
            You can start taking your State Pension from the age of 66.
          </div>
        )
      }

      {/* Saved Rows */}
      {rows.map((row, index) => {
        const rowHasError = isRowInvalid(row);
        const isSP = row.type === "State Pension";
        return (
          <React.Fragment key={index}>
            <div className="no-dovider-dialogBox" />
            <div className="dialog-row dialog-row-inputs">
              <Input2Medium
                value={row.name}
                onChange={(val) => handleRowChange(index, "name", val)}
                options={nameOptions}
                readOnly
                type="invType"
              />
              <Input2Medium
                value={row.type}
                onChange={(val) => handleRowChange(index, "type", val)}
                options={IncomeTypeOptions}
                type="invType"
                readOnly
              />
              <Input2Medium
                value={row.amount.value}
                onChange={(val) => {
                  const isSP = row.type === "State Pension";
                  const unit = row?.amount?.unit ?? "Annual";

                  // normalise the incoming value
                  const num = parseFloat(String(val).replace(/,/g, ""));
                  if (!Number.isFinite(num)) return; // ignore junk

                  // if (isSP) {
                  //   const annual = 11973;
                  //   const monthly = +(annual / 12).toFixed(2);
                  //   const target = unit === "Monthly" ? monthly : annual;

                  //   if (Math.abs(num - target) <= 0.01) {
                  //     handleRowChange(index, "amount", { value: target });
                  //   }
                  //   return;
                  // }

                  // Non-State-Pension: allow update (or add your own range checks here)
                  handleRowChange(index, "amount", { value: num });
                }}
                onUnitChange={(unit) =>
                  handleRowChange(index, "amount", { unit })
                }
                options={TimelineOptions}
                type="number"
                currency
                currentUnit={row.amount?.unit}
              />
              <InputSmall
                value={row.start}
                onChange={(val) => {
                  handleRowChange(index, "start", val);
                }}
                isError={rowHasError}
              />
              <InputSmall
                value={row.end}
                onChange={(val) => handleRowChange(index, "end", val)}
                isError={rowHasError}
                isDisabled={isSP}
              />
              <RemoveButton onClick={() => handleRemove(index)} />
            </div>
            {rowHasError && (
              <div className="error Label">
                Invalid age range. For State Pension, start must be 66 or older.
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default IncomeDialogBox;
